import { AppEnvironmentUtil, cookieConsentSettingsParser, CookieUtil, isObject } from '@as-react/common-utils';
import {
  AemConfiguration,
  Cookie,
  CookieConsentGroups,
  HTTPServerErrorCodes,
  SentryError,
} from '@as-react/common-types';
import { Hub, Scope } from '@sentry/react';
import type { StoreEnhancer } from 'redux';

declare const SITE: { Config: AemConfiguration };

type LoggerOptions = {
  release: string;
};

class RethrownError extends Error {
  original_error;
  stack_before_rethrow;

  constructor(message: string, error: SentryError) {
    super(message);
    this.name = this.constructor.name;
    if (!error) throw new Error('RethrownError requires a message and error');
    this.original_error = error;
    this.stack_before_rethrow = this.stack;
    const message_lines = (this.message.match(/\n/g) || []).length + 1;
    this.stack = `${(this.stack || '')
      .split('\n')
      .slice(0, message_lines + 1)
      .join('\n')}\n${error.stack}`;
  }
}

const SENTRY_DSN_KEY: Record<string, string> = {
  'ecomm-dev': 'fe427258e093fba60968fb48369eb30e',
  'ecomm-acc': '19a9739c74f9ccac37e29b2fa57561e0',
  'ecomm-prd': '918336432c5b58060a2ea0edcc7355c8',
};

class Logger {
  public reduxEnhancer!: StoreEnhancer;
  private SentryInstance: Hub | undefined;

  public async init(options: LoggerOptions) {
    const cookieConsentSettings = cookieConsentSettingsParser(
      decodeURIComponent(CookieUtil.get(Cookie.CONSENT_SETTING) ?? '')
    );
    const shouldMonitorPage =
      SITE.Config?.pageInfo?.isBasketOverviewPage ||
      SITE.Config?.pageInfo?.isBuyPage ||
      SITE.Config?.pageInfo?.isConfirmationPage;
    const canMonitorPage = cookieConsentSettings && cookieConsentSettings[CookieConsentGroups.ANALYTICS];
    if (
      shouldMonitorPage &&
      canMonitorPage &&
      (AppEnvironmentUtil.isProduction() ||
        AppEnvironmentUtil.getName() === 'ecomm-dev' ||
        AppEnvironmentUtil.getName() === 'ecomm-acc')
    ) {
      const Sentry = await import('@sentry/react');
      const { ExtraErrorData } = await import('@sentry/integrations');

      Sentry.init({
        dsn: `https://${
          SENTRY_DSN_KEY[AppEnvironmentUtil.getName()]
        }@o4509044394229760.ingest.de.sentry.io/4509044422738000`,
        integrations: [new ExtraErrorData()],
        normalizeDepth: 3,
        release: `product-lister-react@${options.release}`,
        environment: AppEnvironmentUtil.getName(),
        ignoreErrors: [
          'Saferpay-fields',
          'MutationObserver',
          'Hydration failed',
          'ResizeObserver loop completed',
          'cobrowseIOgetRedactedElementsBounds',
        ],
        denyUrls: [
          /sizebay\.technology/i,
          /reevoo\.com/i,
          /klarna\.com/i,
          /rs\.fullstory\.com/i,
          /ekr\.zdassets\.com/i,
          /stackadapt.com/i,
          /\/(gtm|ga|analytics)\.js/i,
          /google-analytics\.com/i,
          /omtrdc\.net/i,
          /omtrdc\.net/i,
          /www\.saferpay\.com/i,
          /graph\.facebook\.com/i,
          /connect\.facebook\.net\/en_US\/all\.js/i,
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
        ],
        beforeSend: (event, hint) => {
          const originalException = hint.originalException as RethrownError;
          const statusCode = originalException?.original_error?.response?.status;

          if (statusCode && !HTTPServerErrorCodes.includes(statusCode)) {
            return null;
          }

          return event;
        },
      });
      Sentry.setTag('pageType', SITE.Config.analyticsPageType);
      Sentry.setTag('mainWebShop', SITE.Config.mainWebShop);
      Sentry.setTag('isShopLanding', SITE.Config.isShopLanding);

      this.SentryInstance = Sentry as unknown as Hub;
      this.reduxEnhancer = Sentry.createReduxEnhancer({});
    }
  }

  public logError(...data: Array<SentryError>) {
    if (!this.SentryInstance) {
      return;
    }
    const [partA, partB] = data;
    this.SentryInstance.withScope?.((scope: Scope) => {
      let errorToCapture = isObject(partA) ? new Error(partA?.message) : new Error(data.toString());
      let failedRequestData;
      if (
        partA &&
        typeof partA === 'string' &&
        partB &&
        typeof partB === 'object' &&
        this.isErrorFailedRequest(partB)
      ) {
        errorToCapture = new RethrownError(partA, partB);
        failedRequestData = this.mapFailedRequestData(partB);
      } else if (partA && typeof partA === 'object' && this.isErrorFailedRequest(partA)) {
        errorToCapture = new RethrownError(partA.message, partA);
        failedRequestData = this.mapFailedRequestData(partA);
      }

      if (failedRequestData) {
        scope.setExtra('failedRequest', failedRequestData);
        scope.setTag('failed_request', true);
        scope.setTag('failed_request_url', failedRequestData.requestUrl);
        scope.setTag('failed_request_method', failedRequestData.requestMethod);
        scope.setTag('failed_request_response_code', failedRequestData.responseCode);
        scope.setTag('traceId', failedRequestData.responseData?.traceId);
      }
      this.SentryInstance?.captureException(errorToCapture);
    });
  }

  private isErrorFailedRequest(error: SentryError): boolean {
    return !!error.request && !!error.config;
  }

  private mapFailedRequestData(failedRequest: SentryError) {
    const mappedData = {
      requestUrl: failedRequest.config.url,
      requestMethod: failedRequest.config.method,
      requestParams: failedRequest.config.params,
      requestData: failedRequest.config.data,
      responseCode: failedRequest.response?.status,
      responseText: failedRequest.response?.statusText,
      responseData: failedRequest.response?.data,
    };
    return mappedData;
  }
}
const loggerInstance = new Logger();
export default loggerInstance;
